import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import Sheet from './Sheet';
import { api, errorMessage } from '../lib/api';
import { FILTERS } from '../lib/selfie';
import { fmtDate } from '../lib/utils';
import config from '../config';

const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
// signed image paths from the API; same origin in production, the dev server elsewhere
const img = (path) => `${config.apiUrl.replace(/\/$/, '')}${path}`;
const filterName = (key) => FILTERS.find((f) => f.key === key)?.name || '';

/* The selfie wall: every picture someone posted from the photo booth, for
   the whole team. Tap one to see it big; who posted it can take it down. */
export default function SelfieWall({ selfies, onChange }) {
  const [open, setOpen] = useState(null);
  if (!selfies) return null;
  return (
    <section className="mt-6">
      <h2 className="mb-1.5 px-1 text-[13px] font-semibold text-ink2">Selfie wall</h2>
      {selfies.length === 0 ? (
        <p className="rounded-xl bg-surface px-3 py-4 text-center text-[14px] text-ink2">Nobody on the wall yet. Be the first.</p>
      ) : (
        <ul className="grid grid-cols-3 gap-1.5">
          {selfies.map((s) => (
            <li key={s.id}>
              <button type="button" onClick={() => setOpen(s)} className="relative block aspect-square w-full overflow-hidden rounded-lg bg-sunken active:opacity-80" aria-label={`Selfie by ${s.user || 'someone'}`}>
                <img src={img(s.thumb_url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                {s.user && <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-1.5 pb-1 pt-3 text-left text-[11px] font-semibold text-white">{s.user}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <SelfieSheet
          selfie={open}
          onClose={() => setOpen(null)}
          onDeleted={() => {
            setOpen(null);
            onChange?.();
          }}
        />
      )}
    </section>
  );
}

function SelfieSheet({ selfie: s, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const remove = async () => {
    if (!window.confirm('Take this selfie off the wall?')) return;
    setBusy(true);
    try {
      await api.deleteSelfie(s.id);
      onDeleted();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} label="Selfie">
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-cond truncate text-[22px] font-bold leading-none">{s.user || 'Someone'}</p>
            <p className="mt-1 text-[13px] text-ink2">{[fmtDate(s.created_at), timeOf(s.created_at), filterName(s.filter)].filter(Boolean).join(' · ')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>
        <img src={img(s.url)} alt={`Selfie by ${s.user || 'someone'}`} className="mt-3 block max-h-[60vh] w-full rounded-xl object-contain" />
        {error && <p className="mt-2 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
        {s.mine && (
          <button type="button" onClick={remove} disabled={busy} className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sunken text-[14px] font-semibold text-alert active:bg-line disabled:opacity-50">
            <Trash2 className="h-4 w-4" /> Take it off the wall
          </button>
        )}
      </div>
    </Sheet>
  );
}
